/**
 * Pure model + storage helpers for hub social (friends, global/DM chat).
 * Listener audit: hub socket subscriptions live in useSocialLobby; bridge in OnlineLobby.js
 * (lobby_friend_request, lobby_friend_request_resolved, social_invite); game/demo transports unrelated.
 */

export const GLOBAL_LOBBY_CHAT_KEY = 'seka-global-lobby-chat-v1';
export const GLOBAL_LOBBY_DM_CHAT_KEY = 'seka-global-lobby-dm-chat-v1';
export const GLOBAL_LOBBY_CLAN_CHAT_KEY = 'seka-global-lobby-clan-chat-v1';
/** Раньше в этом ключе хранились заявки в друзья; читаем для миграции. */
export const GLOBAL_LOBBY_MAIL_KEY = 'seka-global-lobby-mail-v1';
export const GLOBAL_LOBBY_FRIEND_REQUESTS_KEY = 'seka-global-lobby-friend-requests-v1';

export const SYSTEM_CHAT_SENDER = {
  id: 'system',
  username: 'Система',
  clan: 'SYSTEM',
  level: 99,
  avatarUrl: null,
};

export const CHAT_PARTICIPANTS = [
  { id: 'u-kiryuha', username: 'Kiryuha', clan: 'RONIN', level: 25, avatarUrl: null },
  { id: 'u-igor', username: 'Igor', clan: 'WOLVES', level: 79, avatarUrl: null },
  { id: 'u-mila', username: 'Mila', clan: 'AURORA', level: 32, avatarUrl: null },
];

export const DEFAULT_GLOBAL_LOBBY_MESSAGES = [
  {
    id: 'g-1',
    sender: SYSTEM_CHAT_SENDER,
    text: 'Добро пожаловать в глобальный чат!',
    createdAt: Date.now() - 120000,
  },
  {
    id: 'g-2',
    sender: CHAT_PARTICIPANTS[0],
    text: 'Кто на быстрый матч?',
    createdAt: Date.now() - 80000,
  },
];

export const DEFAULT_DM_LOBBY_MESSAGES = [
  {
    id: 'dm-seed-1',
    peerId: CHAT_PARTICIPANTS[1].id,
    sender: CHAT_PARTICIPANTS[1],
    text: 'Привет! Напиши, когда будешь на месте — сыграем.',
    createdAt: Date.now() - 72_000,
  },
];

function normalizeStoredLobbyMessage(m) {
  if (!m || typeof m !== 'object') return null;
  const text = String(m.text || '').trim();
  if (!text) return null;
  if (m?.sender?.username) {
    return {
      id: m.id || `chat-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      sender: {
        id: m.sender.id || String(m.sender.username).toLowerCase().replace(/\s+/g, '-'),
        username: m.sender.username || 'Игрок',
        clan: m.sender.clan || 'Без клана',
        level: Number(m.sender.level) || 1,
        avatarUrl: m.sender.avatarUrl || null,
      },
      text,
      createdAt: Number(m.createdAt) || Date.now(),
    };
  }
  return {
    id: m?.id || `chat-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    sender: {
      id: String(m?.author || 'player')
        .toLowerCase()
        .replace(/\s+/g, '-'),
      username: String(m?.author || 'Игрок'),
      clan: 'Без клана',
      level: 1,
      avatarUrl: null,
    },
    text,
    createdAt: Number(m?.createdAt) || Date.now(),
  };
}

export function parseLobbyMessagesFromStorage(raw, fallback) {
  try {
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || !parsed.length) return fallback;
    const rows = parsed.map(normalizeStoredLobbyMessage).filter(Boolean);
    return rows.length ? rows : fallback;
  } catch {
    return fallback;
  }
}

function normalizeDmStoredMessage(m) {
  const base = normalizeStoredLobbyMessage(m);
  if (!base) return null;
  const rawPeer = m?.peerId != null ? String(m.peerId).trim() : '';
  const inferred =
    rawPeer ||
    (base.sender?.id && String(base.sender.id) !== 'system' ? String(base.sender.id) : '') ||
    CHAT_PARTICIPANTS[1].id;
  return { ...base, peerId: inferred };
}

export function parseDmMessagesFromStorage(raw, fallback) {
  try {
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || !parsed.length) return fallback;
    const rows = parsed.map(normalizeDmStoredMessage).filter(Boolean);
    return rows.length ? rows : fallback;
  } catch {
    return fallback;
  }
}

export function normalizeIncomingDmServerRow(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const id = String(raw.id || '').trim();
  const peerId = String(raw.peerId || '').trim();
  const text = String(raw.text || '').trim();
  const sender = raw.sender;
  if (!id || !peerId || !text || !sender || typeof sender !== 'object') return null;
  return {
    id,
    peerId,
    sender: {
      id: String(sender.id || 'player'),
      username: String(sender.username || 'Игрок').trim() || 'Игрок',
      clan: String(sender.clan || 'Без клана'),
      level: Math.max(1, Math.floor(Number(sender.level) || 1)),
      avatarUrl: sender.avatarUrl != null ? String(sender.avatarUrl).trim() || null : null,
    },
    text,
    createdAt: Math.floor(Number(raw.createdAt) || Date.now()),
    outgoing: Boolean(raw.outgoing),
  };
}

export function sortChatRowsByCreatedAtThenId(rows) {
  return [...rows].sort((a, b) => {
    const ta = Number(a.createdAt) || 0;
    const tb = Number(b.createdAt) || 0;
    if (ta !== tb) return ta - tb;
    return String(a.id || '').localeCompare(String(b.id || ''));
  });
}

export function loadFriendRequestsFromStorage() {
  try {
    const next = localStorage.getItem(GLOBAL_LOBBY_FRIEND_REQUESTS_KEY);
    if (next) {
      const parsed = JSON.parse(next);
      if (Array.isArray(parsed)) return parsed;
    }
    const legacy = localStorage.getItem(GLOBAL_LOBBY_MAIL_KEY);
    if (legacy) {
      const parsed = JSON.parse(legacy);
      if (Array.isArray(parsed)) {
        try {
          localStorage.setItem(GLOBAL_LOBBY_FRIEND_REQUESTS_KEY, legacy);
        } catch {
          /* ignore */
        }
        return parsed;
      }
    }
  } catch {
    /* ignore */
  }
  return null;
}

export function acceptedFriendsFromRequests(mailFriendRequests, lobbyUser) {
  const uid = String(lobbyUser?.id || '').trim();
  if (!uid) return [];
  const byId = new Map();
  (Array.isArray(mailFriendRequests) ? mailFriendRequests : []).forEach((r) => {
    if (r?.status !== 'accepted') return;
    const fromId = String(r.from?.id || '').trim();
    const toId = String(r.to?.id || '').trim();
    let peer = null;
    if (fromId === uid) peer = r.to;
    else if (toId === uid) peer = r.from;
    if (!peer?.id) return;
    const pid = String(peer.id).trim();
    if (!pid || pid === 'system') return;
    if (!byId.has(pid)) {
      byId.set(pid, {
        id: pid,
        username: String(peer.username || 'Игрок').trim() || 'Игрок',
        clan: peer.clan || 'Без клана',
        level: Number(peer.level) || 1,
        avatarUrl: peer.avatarUrl || null,
      });
    }
  });
  return [...byId.values()];
}

export function friendPendingPairKey(fromId, toId) {
  return `${String(fromId || '').trim()}->${String(toId || '').trim()}`;
}

/** Серверные строки (`fr-srv-*`) + локальные без дублей по паре pending. */
export function mergeFriendRequestsLocalAndServer(prev, serverList, myId) {
  const me = String(myId || '').trim();
  const serverRows = (serverList || []).filter((x) => x && x.id);
  const byServerId = new Set(serverRows.map((r) => String(r.id)));

  const pendingOutgoing = new Set();
  const pendingIncoming = new Set();
  for (const r of serverRows) {
    if (r.status !== 'pending') continue;
    const fromId = String(r.from?.id || '');
    const toId = String(r.to?.id || '');
    if (fromId && toId) pendingOutgoing.add(friendPendingPairKey(fromId, toId));
    if (fromId && toId && me && String(r.to?.id) === me) pendingIncoming.add(friendPendingPairKey(fromId, toId));
  }

  const out = [...serverRows.map((r) => ({ ...r }))];
  for (const r of prev || []) {
    const rid = String(r?.id || '');
    if (!rid) continue;
    if (byServerId.has(rid)) continue;
    if (rid.startsWith('fr-srv-')) continue;
    if (r.status === 'pending' && r.type === 'outgoing' && String(r.from?.id) === me) {
      if (pendingOutgoing.has(friendPendingPairKey(r.from.id, r.to?.id))) continue;
    }
    if (r.status === 'pending' && r.type === 'incoming' && String(r.to?.id) === me) {
      if (pendingIncoming.has(friendPendingPairKey(r.from?.id, r.to?.id))) continue;
    }
    out.push(r);
  }
  out.sort((a, b) => (Number(b.createdAt) || 0) - (Number(a.createdAt) || 0));
  return out.slice(0, 200);
}

/**
 * @param {{ getDevDeviceId: () => string, devUserProfile: { username: string, level: number } }} opts
 */
export function getInitialMailFriendRequests(opts) {
  if (typeof window === 'undefined') return [];
  try {
    if (localStorage.getItem('token')) return [];
  } catch {
    /* ignore */
  }
  const loaded = loadFriendRequestsFromStorage();
  if (loaded && loaded.length) return loaded;
  const getDevDeviceId = opts?.getDevDeviceId;
  const devUserProfile = opts?.devUserProfile || { username: 'Гость', level: 1 };
  const devTo = {
    id: typeof getDevDeviceId === 'function' ? getDevDeviceId() : 'dev_local',
    username: devUserProfile.username,
    clan: 'Без клана',
    level: devUserProfile.level,
    avatarUrl: null,
  };
  return [
    {
      id: `fr-seed-${Date.now()}`,
      from: CHAT_PARTICIPANTS[0],
      to: devTo,
      status: 'pending',
      type: 'incoming',
      seen: false,
      createdAt: Date.now() - 90_000,
    },
    {
      id: `fr-seed-accepted-${Date.now()}`,
      from: CHAT_PARTICIPANTS[1],
      to: devTo,
      status: 'accepted',
      type: 'incoming',
      seen: true,
      createdAt: Date.now() - 200_000,
    },
  ];
}

/** Удалить принятую «братву» с peerId (локальный демо-слой). */
export function senderFromCurrentUserLobby(u) {
  return {
    id: u?.id || 'local-player',
    username: u?.username || 'Вы',
    clan: u?.clan || 'Без клана',
    level: Number(u?.level) || 1,
    avatarUrl: u?.avatarUrl || null,
  };
}

export function filterOutAcceptedFriendWithPeer(prev, peerId, myId) {
  const pid = String(peerId || '').trim();
  const me = String(myId || '').trim();
  if (!pid || !me) return prev || [];
  return (prev || []).filter((r) => {
    if (r?.status !== 'accepted') return true;
    const fromId = String(r.from?.id || '').trim();
    const toId = String(r.to?.id || '').trim();
    const other = fromId === me ? toId : toId === me ? fromId : null;
    return String(other || '') !== pid;
  });
}
